// Suppress known dev-only Vue warnings that are non-actionable in this project.
//
// Vue's dev-mode warnings include several "check-only" cases that don't affect
// runtime (Vue itself stamps them with "*The DOM will not be rectified in
// production due to performance overhead*"). The ones filtered below are
// either rooted in Nuxt internals we don't control or in known web-component
// hydration quirks — keeping them in the console drowns out real warnings.
//
// Filters are intentionally NARROW: each one matches a specific message
// substring + (where relevant) a structural marker in the component trace, so
// a genuinely new hydration mismatch in app code still reaches the console.
export default defineNuxtPlugin((nuxtApp) => {
  if (!import.meta.dev) return;

  const prev = nuxtApp.vueApp.config.warnHandler;
  nuxtApp.vueApp.config.warnHandler = (msg, instance, trace) => {
    // 1. Nuxt uses <Suspense> internally; this warning is from Vue's dev build only.
    if (msg.includes("<Suspense> is an experimental feature")) return;

    // 2. RouterLink / NuxtLink active-class hydration mismatch.
    // Vue Router's `useLink()` doesn't resolve the link-active state on the
    // server pass for some link configurations (logo links to a locale root
    // are the canonical case), so SSR renders `class=""` and CSR adds
    // `router-link-active router-link-exact-active`. We override these to
    // empty strings on the logo link itself, but the warning still fires
    // on intermediate components — purely cosmetic, no runtime impact.
    if (
      msg.includes("Hydration class mismatch") &&
      msg.includes("router-link-active")
    ) {
      return;
    }

    // 3. AsyncComponentWrapper / Suspense Comment-vs-node hydration mismatch.
    // When a page's setup awaits async data, Vue renders a Comment placeholder
    // in the AsyncComponentWrapper slot on the server and the resolved subtree
    // on the client. Vue then reports it as a node mismatch on the page
    // component (here `<Index>`, `<...slug>`). Source-level guards inside
    // pages can't reach this boundary; the rendered output is correct.
    if (
      msg.includes("Hydration node mismatch") &&
      typeof trace === "string" &&
      trace.includes("AsyncComponentWrapper")
    ) {
      return;
    }

    if (prev) {
      prev(msg, instance, trace);
    } else {
      // eslint-disable-next-line no-console
      console.warn("[Vue warn]:", msg, "\n", trace);
    }
  };
});
